import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DB } from '@services/database.service';
import { EVENT_TYPES } from '@shared/constants/event-types.v4';
import { useUIStore } from '@stores/v4/useUIStore';
import { Event } from '@/types/zod.schemas';
import { HistoryCompute } from '@/shared/logic/history-logic';
import { toLogicDate } from '@/utils/date-utils';

interface HistoryState {
    events: Event[];
    dailyVolumes: Record<string, number>;
    uniqueExercises: any[];
    activeHistorySession: any | null;
    expandedActivityId: string | null;
    analyzingEvents: Set<string>;
}

interface HistoryActions {
    setExpandedActivityId: (id: string | null) => void;
    fetchGlobalHistory: (force?: boolean) => Promise<void>;
    fetchUniqueExercises: () => Promise<void>;
    getEventsForDate: (dateObj: Date) => any[];
    getEventsForDateRange: (start: Date, end: Date) => any[];
    getHistoryStats: () => any;
    // Optimistic UI Actions
    addOptimisticEvent: (event: Event) => void;
    removeOptimisticEvent: (eventId: string) => void;
    updateOptimisticEvent: (eventId: string, updates: Partial<Event>) => void;
}

/**
 * useHistoryStore: Tactical persistence for the Unified Event Stream.
 * HARDENED: Modularized (< 150 lines). Orchestration moved to HistoryOrchestrator.
 * ENHANCED: Supports Optimistic UI updates for 0ms interaction latency.
 */
export const useHistoryStore = create<HistoryState & HistoryActions>()(
    persist(
        (set, get) => ({
            events: [],
            dailyVolumes: {},
            uniqueExercises: [],
            activeHistorySession: null,
            expandedActivityId: null,
            analyzingEvents: new Set(),

            setExpandedActivityId: (id) => set({ expandedActivityId: id }),

            fetchGlobalHistory: async (force = false) => {
                const currentEvents = get().events;
                const isLoading = useUIStore.getState().isLoading;

                // Only skip if already loading to prevent race conditions
                if (isLoading) return;

                if (force) useUIStore.getState().setIsLoading(true);
                try {
                    const { data: userData } = await DB.getUser();
                    if (!userData?.user) return;
                    
                    if (force || currentEvents.length === 0) await DB.autoSkipPastEvents();

                    const { data: events, error } = await DB.fetchEvents(userData.user.id);
                    if (error) throw error;

                    set({ events: [...(events || [])] as any });
                } catch (err) {
                    console.error("[HistoryStore] fetchGlobalHistory FAILED:", err);
                } finally {
                    if (force) useUIStore.getState().setIsLoading(false);
                }
            },

            fetchUniqueExercises: async () => {
                if (get().uniqueExercises.length > 0) return;
                try {
                    const { data } = await DB.fetchExerciseLibrary();
                    set({ uniqueExercises: data || [] });
                } catch (err) {
                    console.error("[HistoryStore] fetchUniqueExercises FAILED:", err);
                }
            },

            getEventsForDateRange: (start, end) => {
                const sStr = toLogicDate(start);
                const eStr = toLogicDate(end);
                return (get().events || []).filter(e => {
                    const d = toLogicDate(e.start_time);
                    return d >= sStr && d <= eStr;
                });
            },

            getEventsForDate: (dateObj) => {
                const activePerformance = (window as any).useSessionStore?.getState()?.activeSession;
                const activeRecovery = (window as any).useRecoveryStore?.getState()?.activeRecoverySession;
                // FIX: HistoryCompute.getEventsForDate handles toLogicDate internally
                return HistoryCompute.getEventsForDate(get().events, dateObj, activePerformance, activeRecovery);
            },

            getHistoryStats: () => HistoryCompute.calculateHistoryStats(get().events),

            addOptimisticEvent: (event) => set(state => ({
                events: [event, ...state.events].sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
            })),

            removeOptimisticEvent: (eventId) => set(state => ({
                events: state.events.filter(e => e.id !== eventId)
            })),

            updateOptimisticEvent: (eventId, updates) => set(state => ({
                events: state.events.map(e => e.id === eventId ? { ...e, ...updates } : e)
            }))
        }),
        { 
            name: 'mp-history-cache',
            partialize: (state) => ({ 
                events: state.events,
                dailyVolumes: state.dailyVolumes 
            })
        }
    )
);

if (typeof window !== 'undefined') {
    (window as any).useHistoryStore = useHistoryStore;
}
